import {
  PdMeasurementFlow,
  ShMeasurementFlow,
  shSampleSpreadMaxMm,
} from '@ca/optical-measure/flow'
import type {
  PdMeasurementResult,
  ShMeasurementResult,
  ShSampleSpreadCheck,
  ShVboxRangeCheck,
} from '@ca/optical-measure/flow'
import { Portal } from '@gorhom/portal'
import AppConfig from 'config'
import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { View } from 'react-native'
import {
  MeasurementContext,
  type ActiveMeasurement,
} from './MeasurementContext'
import { useShAdjustmentRetries } from './useMeasurementMethod'

export function MeasurementProvider({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation(['measurement', 'ui'])
  const shAdjustmentRetries = useShAdjustmentRetries()
  const [activeMeasurement, setActiveMeasurement] =
    useState<ActiveMeasurement | null>(null)
  // The remaining flow strings still fall back to the package defaults.
  const messages = {
    instructions: t('measurement:app.instructions.header.title'),
    close: t('ui:close'),
  }

  // A formatter rather than a string: the lens height can be changed mid-flow,
  // so the bounds only exist at the point the warning is shown.
  const formatOutOfRangeWarning = ({ minMm, maxMm }: ShVboxRangeCheck) =>
    t('measurement:app.review.sh.warnings.outOfVboxRange', {
      min: minMm,
      max: maxMm,
    })

  // The spread always carries a decimal and the separator is a comma in most of
  // our locales, so it is formatted here rather than handed to i18next raw.
  const formatUnstableWarning = (check: ShSampleSpreadCheck) =>
    t('measurement:app.review.sh.warnings.unstableCapture', {
      spread: new Intl.NumberFormat(i18n.language, {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      }).format(shSampleSpreadMaxMm(check)),
    })

  const close = () => setActiveMeasurement(null)

  const handlePdComplete = (result: PdMeasurementResult) => {
    if (activeMeasurement?.type !== 'pd') return
    const onComplete = activeMeasurement.onComplete
    close()
    onComplete(result)
  }

  const handleShComplete = (result: ShMeasurementResult) => {
    if (activeMeasurement?.type !== 'sh') return
    const onComplete = activeMeasurement.onComplete
    close()
    onComplete(result)
  }

  return (
    <MeasurementContext.Provider value={{ open: setActiveMeasurement }}>
      {children}
      {activeMeasurement && (
        <Portal hostName="root">
          <View className="absolute inset-0 bg-black">
            {activeMeasurement.type === 'pd' ? (
              <PdMeasurementFlow
                messages={messages}
                isDev={AppConfig.isDev}
                isDebug={AppConfig.isDebug}
                onComplete={handlePdComplete}
                onCancel={close}
              />
            ) : (
              <ShMeasurementFlow
                vboxMm={activeMeasurement.vboxMm}
                isDev={AppConfig.isDev}
                isDebug={AppConfig.isDebug}
                adjustmentRetries={shAdjustmentRetries}
                messages={messages}
                formatOutOfRangeWarning={formatOutOfRangeWarning}
                formatUnstableWarning={formatUnstableWarning}
                onComplete={handleShComplete}
                onCancel={close}
              />
            )}
          </View>
        </Portal>
      )}
    </MeasurementContext.Provider>
  )
}
